import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, format } from "date-fns";
import { dfLocale as fr } from "../../lib/i18n";
import { ChevronLeft, ChevronRight, Navigation, Car, Clock, RefreshCw, LocateFixed, WifiOff, Sparkles } from "lucide-react";
import { cachedGet } from "../../lib/offline";
import { ItineraryMap } from "./ItineraryMap";

const iso = (d) => format(d, "yyyy-MM-dd");
const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}` : `${m} min`);
const KIND = { depart: "Départ", chargement: "Chargement", livraison: "Livraison" };
const DOT = { depart: "bg-slate-900", chargement: "bg-[#1559d6]", livraison: "bg-teal-600" };

const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition((p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }), () => resolve(null), { timeout: 8000, maximumAge: 120000 });
});

function Leg({ leg }) {
  return (
    <div className="ml-[14px] flex items-center gap-3 border-l-2 border-dashed border-slate-300 py-3 pl-6 text-xs font-semibold text-slate-600" data-testid={`itinerary-leg-${leg.to}`}>
      <Car className="h-4 w-4 text-slate-400" />
      <span className="font-mono-num" data-testid={`itinerary-leg-duration-${leg.to}`}>{fmtMin(leg.duration_min)}</span>
      <span className="text-slate-400">·</span>
      <span className="font-mono-num">{leg.distance_km} km</span>
    </div>
  );
}

function Stop({ s, n }) {
  const nav = s.point && s.kind !== "depart" && (
    <div className="mt-3 flex gap-2">
      <a data-testid={`itinerary-waze-${n}`} href={`https://waze.com/ul?ll=${s.point[0]},${s.point[1]}&navigate=yes`} target="_blank" rel="noreferrer" className="rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700">Waze</a>
      <a data-testid={`itinerary-gmaps-${n}`} href={`https://www.google.com/maps/dir/?api=1&destination=${s.point[0]},${s.point[1]}`} target="_blank" rel="noreferrer" className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">Google Maps</a>
    </div>
  );
  return (
    <div className="flex gap-3" data-testid={`itinerary-stop-${n}`}>
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${DOT[s.kind]}`}>{s.kind === "depart" ? "D" : n}</div>
      <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{KIND[s.kind]}{s.time ? ` · ${s.time}` : ""}</p>
          {s.move_id && <Link to={`/nomade/chantier/${s.move_id}`} className="text-xs font-semibold text-[#1559d6]" data-testid={`itinerary-open-move-${n}`}>Fiche chantier</Link>}
        </div>
        <p className="mt-1 font-semibold text-slate-900">{s.label}</p>
        {s.address && <p className="text-sm text-slate-600">{s.address}</p>}
        {!s.point && s.kind !== "depart" && <p className="mt-1 text-xs text-amber-700">Adresse non localisée — exclue du calcul</p>}
        {nav}
      </div>
    </div>
  );
}

function Summary({ data, offline }) {
  const chantiers = new Set(data.stops.filter((s) => s.move_id).map((s) => s.move_id)).size;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="itinerary-summary">
      {[["Chantiers", chantiers, "itinerary-total-moves"], ["Distance", `${data.total_km} km`, "itinerary-total-km"], ["Conduite", fmtMin(data.total_min), "itinerary-total-time"]].map(([l, v, id]) => (
        <div key={id} className="rounded-2xl bg-[#0b2c6b] p-3 text-white">
          <p className="text-[10px] uppercase tracking-widest text-blue-200">{l}</p>
          <p className="font-mono-num text-lg font-bold" data-testid={id}>{v}</p>
        </div>
      ))}
      {offline && <p className="col-span-3 flex items-center gap-1 text-xs text-slate-500"><WifiOff className="h-3 w-3" />Itinéraire enregistré sur l'appareil (hors ligne)</p>}
    </div>
  );
}

function OrderToggle({ data, view, setView }) {
  if (!data.optimized) {
    return data.optimization_note ? <p className="flex items-center gap-1 text-xs text-slate-500" data-testid="itinerary-optim-note"><Sparkles className="h-3 w-3" />{data.optimization_note}</p> : null;
  }
  const o = data.optimized;
  return (
    <div className="space-y-2" data-testid="itinerary-optim">
      <p className="flex items-center gap-1.5 rounded-xl bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800" data-testid="itinerary-optim-saving">
        <Sparkles className="h-4 w-4" />Ordre optimisé : {fmtMin(o.saved_min)} et {o.saved_km} km de route en moins, sans retard sur les rendez-vous
      </p>
      <div className="flex rounded-xl bg-slate-200/70 p-1">
        {[["planned", "Ordre prévu"], ["optimized", "Ordre optimisé"]].map(([k, l]) => (
          <button key={k} data-testid={`itinerary-view-${k}`} onClick={() => setView(k)} className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${view === k ? "bg-white text-[#0b2c6b] shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export default function NomadeItinerary() {
  const [view, setView] = useState("planned");
  const [day, setDay] = useState(new Date());
  const [state, setState] = useState({ data: null, offline: false, gps: null, loading: true });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const pos = await getPosition();
    try {
      const r = await cachedGet("/field/itinerary", { date: iso(day), ...(pos || {}) }, `date=${iso(day)}`);
      setState({ data: r.data, offline: r.offline, gps: !!pos, loading: false });
      setView("planned");
    } catch (e) {
      setState({ data: null, offline: true, gps: !!pos, loading: false });
    }
  }, [day]);
  useEffect(() => {
    load();
  }, [load]);

  const { data: raw, loading } = state;
  const data = raw && view === "optimized" && raw.optimized ? { ...raw, ...raw.optimized } : raw;
  const legTo = Object.fromEntries((data?.legs || []).map((l) => [l.to, l]));
  const offset = data?.stops[0]?.kind === "depart" ? 0 : 1;
  return (
    <div className="fade-up space-y-4" data-testid="nomade-itinerary">
      <div className="flex items-center justify-between">
        <button data-testid="itinerary-prev" onClick={() => setDay(addDays(day, -1))} className="rounded-lg p-2 hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={() => setDay(new Date())} className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-teal-600">Itinéraire du jour</p>
          <h1 className="text-lg font-bold capitalize text-slate-900" data-testid="itinerary-date-label">{format(day, "EEEE d MMMM", { locale: fr })}</h1>
        </button>
        <button data-testid="itinerary-next" onClick={() => setDay(addDays(day, 1))} className="rounded-lg p-2 hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1" data-testid="itinerary-gps-status"><LocateFixed className="h-3.5 w-3.5" />{state.gps ? "Départ depuis votre position GPS" : "GPS indisponible — départ au 1er chantier"}</span>
        <button data-testid="itinerary-refresh" onClick={load} className="flex items-center gap-1 rounded-lg bg-white px-2 py-1 font-semibold text-[#0b2c6b]"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Recalculer</button>
      </div>
      {loading && !data && <p className="py-12 text-center text-sm text-slate-500" data-testid="itinerary-loading"><Clock className="mx-auto mb-2 h-5 w-5 animate-pulse" />Calcul de l'itinéraire…</p>}
      {!loading && !data && <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-500" data-testid="itinerary-error">Itinéraire indisponible hors ligne pour ce jour</p>}
      {data && !data.stops.some((s) => s.move_id) && (
        <p className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500" data-testid="itinerary-empty">
          {data.linked ? "Aucun chantier ne vous est assigné ce jour" : "Votre compte n'est lié à aucun membre d'équipe. Demandez au bureau de le lier dans Équipes & véhicules."}
        </p>
      )}
      {data && data.stops.some((s) => s.move_id) && (
        <>
          <OrderToggle data={raw} view={view} setView={setView} />
          <Summary data={data} offline={state.offline} />
          <ItineraryMap stops={data.stops} geometry={data.geometry} />
          {!data.routed && <p className="flex items-center gap-1 text-xs text-amber-700" data-testid="itinerary-no-route"><Navigation className="h-3 w-3" />Temps de trajet indisponible (adresses non localisées ou service injoignable)</p>}
          <div>
            {data.stops.map((s, i) => (
              <div key={i}>
                {legTo[i] && <Leg leg={legTo[i]} />}
                <Stop s={s} n={i + offset} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
